/** @jsx jsx */
import { gql, useMutation } from '@apollo/client';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  MeasuringStrategy,
  PointerSensor,
  defaultDropAnimation,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { jsx } from '@emotion/react';
import * as Sentry from '@sentry/react';
import React from 'react';
import { createPortal } from 'react-dom';

import { useShowToast } from 'src/components/toast/Toast';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { customCollisionDetection } from './dragAndDropHelpers';

interface DroppableId {
  type: 'collection' | 'collectionEntry';
  id: string;
}
export function printDroppableId(id: DroppableId): string {
  return `${id.type}:${id.id}`;
}
function parseDroppableId(id: string): DroppableId {
  const match = id.match(/(collection|collectionEntry):(.*)/);
  if (
    !isMinLengthArray(3, match) ||
    (match[1] !== 'collection' && match[1] !== 'collectionEntry')
  ) {
    throw new Error('Unparseable drop id');
  }
  return { type: match[1], id: match[2] };
}

type DropData = { index: number; containerId: string } | undefined;

// This is the 'safe' measuring strategy, in that it remeasures at each
// possible trigger. This doesn't seem to affect performance, but seems the
// safest option given a number of elements here can resize
const MEASURING_STRATEGY = {
  droppable: {
    strategy: MeasuringStrategy.Always,
  },
};

export const CollectionSortableContext = ({
  children,
  collection,
}: {
  children: React.ReactNode;
  collection: GraphQLTypes.CollectionEntryOperationCollectionFragment;
}) => {
  const items = React.useMemo(
    () =>
      collection.operations.map((operation) =>
        printDroppableId({ type: 'collectionEntry', id: operation.id }),
      ),
    [collection],
  );
  return (
    <SortableContext
      items={items}
      strategy={verticalListSortingStrategy}
      id={printDroppableId({ type: 'collection', id: collection.id })}
    >
      {children}
    </SortableContext>
  );
};

export const CollectionsDndContext = ({
  children,
  allCollections,
  activeDragCollectionEntryId,
  setActiveDragCollectionEntryId,
  setDragOverride,
  dragAndDropRef,
  CollectionEntryDrag,
}: {
  children: React.ReactNode;
  allCollections: GraphQLTypes.CollectionEntryOperationCollectionFragment[];
  activeDragCollectionEntryId: string | null;
  setActiveDragCollectionEntryId: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  setDragOverride: React.Dispatch<
    React.SetStateAction<
      | {
          sourceCollectionId: string;
          targetCollectionId: string;
          collectionEntryId: string;
        }
      | undefined
    >
  >;
  dragAndDropRef: React.RefObject<HTMLDivElement>;
  CollectionEntryDrag: (props: {
    collectionName: string;
    dropCursorStyle: 'not-allowed' | 'grabbing' | 'copy';
  }) => React.ReactElement;
}) => {
  const { showToasts } = useShowToast();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 15 } }),
  );

  const [moveEntryBetweenCollections] = useMutation<
    GraphQLTypes.MoveEntryBetweenCollections,
    GraphQLTypes.MoveEntryBetweenCollectionsVariables
  >(
    gql`
      mutation MoveEntryBetweenCollections(
        $collectionEntryId: ID!
        $originCollectionId: ID!
        $targetCollectionId: ID!
        $lowerOrderingBound: String
        $upperOrderingBound: String
      ) {
        operationCollection(id: $originCollectionId) {
          operation(id: $collectionEntryId) {
            ... on OperationCollectionEntryMutation {
              moveToCollection(
                collectionId: $targetCollectionId
                lowerOrderingBound: $lowerOrderingBound
                upperOrderingBound: $upperOrderingBound
              ) {
                ... on MoveOperationCollectionEntrySuccess {
                  originCollection {
                    id
                    operations {
                      id
                      orderingIndex
                    }
                  }
                  targetCollection {
                    id
                    operations {
                      id
                      orderingIndex
                    }
                  }
                }
                ... on Error {
                  message
                }
              }
            }
            ... on Error {
              message
            }
          }
        }
      }
    `,
    {
      onError: () => {
        showToasts({
          heading: 'Error moving operation',
          level: 'error',
          message: false,
        });
      },
    },
  );

  const [updateCollectionEntryOrder] = useMutation<
    GraphQLTypes.UpdateCollectionEntryOrder,
    GraphQLTypes.UpdateCollectionEntryOrderVariables
  >(
    gql`
      mutation UpdateCollectionEntryOrder(
        $collectionEntryId: ID!
        $collectionId: ID!
        $lowerOrderingBound: String
        $upperOrderingBound: String
      ) {
        operationCollection(id: $collectionId) {
          operation(id: $collectionEntryId) {
            ... on OperationCollectionEntryMutation {
              reorderEntry(
                lowerOrderingBound: $lowerOrderingBound
                upperOrderingBound: $upperOrderingBound
              ) {
                ... on OperationCollection {
                  id
                  operations {
                    id
                    orderingIndex
                  }
                }
                ... on Error {
                  message
                }
              }
            }
            ... on Error {
              message
            }
          }
        }
      }
    `,
    {
      onError: () => {
        showToasts({
          heading: 'Error moving operation',
          level: 'error',
          message: false,
        });
      },
    },
  );

  const allCollectionsByEntryIds = React.useMemo(
    () =>
      Object.fromEntries(
        allCollections.flatMap((collection) =>
          collection.operations.map((collectionEntry) => [
            collectionEntry.id,
            collection,
          ]),
        ),
      ),
    [allCollections],
  );
  const allCollectionEntriesById = React.useMemo(
    () =>
      Object.fromEntries(
        allCollections.flatMap((collection) =>
          collection.operations.map((collectionEntry) => [
            collectionEntry.id,
            collectionEntry,
          ]),
        ),
      ),
    [allCollections],
  );

  const [dropCursorStyle, setDropCursorStyle] = React.useState<
    'not-allowed' | 'grabbing' | 'copy'
  >('grabbing');

  const onDragStart = React.useCallback(
    ({ active: origin }) => {
      setActiveDragCollectionEntryId(parseDroppableId(origin.id).id);
    },
    [setActiveDragCollectionEntryId],
  );

  const onDragCancel = React.useCallback(() => {
    setActiveDragCollectionEntryId(null);
    setDragOverride(undefined);
  }, [setActiveDragCollectionEntryId, setDragOverride]);

  const onDragEnd = React.useCallback(
    async ({
      active: origin,
      over: target,
    }: DragEndEvent & {
      target: null | { data?: { current?: { sortable: DropData } } };
    }) => {
      setDragOverride(undefined);
      setActiveDragCollectionEntryId(null);

      const movedCollectionEntryId = parseDroppableId(origin.id).id;
      const originCollection = allCollectionsByEntryIds[movedCollectionEntryId];
      if (!originCollection) {
        throw new Error('Could not find original collection');
      }
      const originIndex = originCollection.operations.findIndex(
        (collectionEntry) => collectionEntry.id === movedCollectionEntryId,
      );
      const movedCollectionEntry = originCollection.operations[originIndex];
      if (!movedCollectionEntry) {
        throw new Error('Expected source index to be in operations array');
      }

      const targetDroppableId = target?.id;
      const targetData = target?.data.current?.sortable;
      if (!targetDroppableId) {
        return;
      }

      const parsedTargetDroppableId = parseDroppableId(targetDroppableId);
      const targetCollectionId =
        parsedTargetDroppableId.type === 'collection'
          ? parsedTargetDroppableId.id
          : targetData?.containerId &&
            parseDroppableId(targetData.containerId).id;
      const targetCollection = allCollections.find(
        ({ id }) => id === targetCollectionId,
      );
      if (!targetCollection) {
        throw new Error('Could not find target collection');
      }
      if (!targetCollection.permissions.canEditOperations) {
        return;
      }
      const targetIndex = targetData?.index;

      if (targetCollection.id === originCollection.id) {
        // Noop actions
        if (
          parsedTargetDroppableId.type === 'collection' ||
          originIndex === targetIndex
        ) {
          return;
        }

        if (originIndex === undefined || targetIndex === undefined) {
          throw new Error('Expected source and target index for drag');
        }

        const newOperations = [...originCollection.operations];
        newOperations.splice(originIndex, 1);
        newOperations.splice(targetIndex, 0, movedCollectionEntry);

        const movingLater = originIndex < targetIndex;
        const lowerIndex = movingLater ? targetIndex : targetIndex - 1;
        const upperIndex = movingLater ? targetIndex + 1 : targetIndex;

        const reorderResult = (
          await updateCollectionEntryOrder({
            variables: {
              collectionId: originCollection.id,
              collectionEntryId: movedCollectionEntryId,
              lowerOrderingBound:
                originCollection.operations[lowerIndex]?.orderingIndex,
              upperOrderingBound:
                originCollection.operations[upperIndex]?.orderingIndex,
            },
            optimisticResponse: {
              operationCollection: {
                __typename: 'OperationCollectionMutation',
                operation: {
                  __typename: 'OperationCollectionEntryMutation',
                  reorderEntry: {
                    __typename: 'OperationCollection',
                    id: originCollection.id,
                    operations: newOperations,
                  },
                },
              },
            },
          })
        ).data?.operationCollection?.operation;

        if (
          reorderResult &&
          reorderResult.__typename !== 'OperationCollectionEntryMutation'
        ) {
          Sentry.captureMessage(reorderResult.message);
          showToasts({
            heading: `Error moving operation`,
            level: 'error',
            message: reorderResult.message,
          });
        } else if (
          reorderResult?.reorderEntry &&
          reorderResult.reorderEntry.__typename !== 'OperationCollection'
        ) {
          Sentry.captureMessage(reorderResult.reorderEntry.message);
          showToasts({
            heading: `Error moving operation`,
            level: 'error',
            message: reorderResult.reorderEntry.message,
          });
        }
      } else if (
        targetCollection.operations.some(
          (o) => o.name === movedCollectionEntry.name,
        )
      ) {
        showToasts({
          heading: `Error moving operation`,
          level: 'error',
          message: `Operation name "${
            movedCollectionEntry.name
          }" already exists in collection "${
            targetCollection.isSandbox
              ? 'Sandbox'
              : targetCollection.isShared
                ? 'Shared'
                : 'Personal'
          }/${targetCollection.name}".`,
        });
      } else {
        let reorderBounds: { upper: number; lower: number } | undefined;
        if (parsedTargetDroppableId.type === 'collectionEntry') {
          if (originIndex === undefined || targetIndex === undefined) {
            throw new Error('Expected source and target index for drag');
          }
          reorderBounds = {
            lower: targetIndex - 1,
            upper: targetIndex,
          };
        }

        const newOperations = [...targetCollection.operations];
        newOperations.splice(
          targetIndex ?? targetCollection.operations.length,
          0,
          movedCollectionEntry,
        );

        const moveResult = (
          await moveEntryBetweenCollections({
            variables: {
              collectionEntryId: movedCollectionEntryId,
              originCollectionId: originCollection.id,
              targetCollectionId: targetCollection.id,
              lowerOrderingBound:
                reorderBounds &&
                targetCollection.operations[reorderBounds?.lower]
                  ?.orderingIndex,
              upperOrderingBound:
                reorderBounds &&
                targetCollection.operations[reorderBounds?.upper]
                  ?.orderingIndex,
            },
            optimisticResponse: {
              operationCollection: {
                __typename: 'OperationCollectionMutation',
                operation: {
                  __typename: 'OperationCollectionEntryMutation',
                  moveToCollection: {
                    __typename: 'MoveOperationCollectionEntrySuccess',
                    originCollection: {
                      __typename: 'OperationCollection',
                      id: originCollection.id,
                      operations: originCollection.operations.filter(
                        (operation) => operation.id !== movedCollectionEntry.id,
                      ),
                    },
                    targetCollection: {
                      __typename: 'OperationCollection',
                      id: targetCollection.id,
                      operations: newOperations,
                    },
                  },
                },
              },
            },
          })
        ).data?.operationCollection?.operation;

        if (
          moveResult &&
          moveResult.__typename !== 'OperationCollectionEntryMutation'
        ) {
          Sentry.captureMessage(moveResult.message);
          showToasts({
            heading: `Error moving operation`,
            level: 'error',
            message: moveResult.message,
          });
        } else if (
          moveResult &&
          moveResult.moveToCollection.__typename !==
            'MoveOperationCollectionEntrySuccess'
        ) {
          Sentry.captureMessage(moveResult.moveToCollection.message);
          showToasts({
            heading: `Error moving operation`,
            level: 'error',
            message: moveResult.moveToCollection.message,
          });
        }
      }
    },
    [
      setDragOverride,
      setActiveDragCollectionEntryId,
      allCollectionsByEntryIds,
      allCollections,
      updateCollectionEntryOrder,
      showToasts,
      moveEntryBetweenCollections,
    ],
  );

  const onDragOver = React.useCallback(
    ({
      active: origin,
      over: target,
    }: DragOverEvent & {
      target: null | { data?: { current?: { sortable: DropData } } };
    }) => {
      const targetDroppableId = target?.id;
      const targetData = target?.data.current?.sortable;
      if (!targetDroppableId) {
        setDropCursorStyle('grabbing');
        return;
      }
      const parsedTargetDroppableId = parseDroppableId(targetDroppableId);
      const targetCollectionId =
        parsedTargetDroppableId.type === 'collection'
          ? parsedTargetDroppableId.id
          : targetData?.containerId &&
            parseDroppableId(targetData.containerId).id;

      const targetCollection = allCollections.find(
        ({ id }) => id === targetCollectionId,
      );

      const movedCollectionEntryId = parseDroppableId(origin.id).id;
      const sourceCollectionId =
        allCollectionsByEntryIds[movedCollectionEntryId]?.id;

      if (!targetCollectionId || !sourceCollectionId) {
        setDropCursorStyle('grabbing');
        return;
      }

      setDropCursorStyle(
        !targetCollection || targetCollectionId === sourceCollectionId
          ? 'grabbing'
          : targetCollection.permissions.canEditOperations
            ? 'copy'
            : 'not-allowed',
      );

      if (
        parsedTargetDroppableId.type === 'collectionEntry' &&
        targetCollectionId !== sourceCollectionId &&
        targetCollection?.permissions.canEditOperations
      ) {
        setDragOverride((current) =>
          current?.targetCollectionId === targetCollectionId
            ? current
            : {
                collectionEntryId: movedCollectionEntryId,
                sourceCollectionId,
                targetCollectionId,
              },
        );
      } else if (target.id !== origin.id) {
        setDragOverride(undefined);
      }
    },
    [allCollectionsByEntryIds, setDragOverride, allCollections],
  );

  const activeDragCollectionEntry =
    activeDragCollectionEntryId &&
    allCollectionEntriesById[activeDragCollectionEntryId];

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      measuring={MEASURING_STRATEGY}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragCancel={onDragCancel}
    >
      {children}
      {dragAndDropRef.current &&
        createPortal(
          <DragOverlay adjustScale={false} dropAnimation={defaultDropAnimation}>
            {activeDragCollectionEntry ? (
              <CollectionEntryDrag
                collectionName={activeDragCollectionEntry.name}
                dropCursorStyle={dropCursorStyle}
              />
            ) : null}
          </DragOverlay>,
          dragAndDropRef.current,
        )}
    </DndContext>
  );
};
